import React, { useState } from 'react';
import { RotateCcw, Trophy, AlertCircle, Crown, Brain, Send } from 'lucide-react';
import { API_URL } from '../utils/gameUtils';

function ApplicationRow({ item, userId }) {
  const [status, setStatus] = useState('idle'); // idle, applying, done, error
  const [errorDetail, setErrorDetail] = useState('');

  const handleApply = async () => {
    setStatus('applying');
    setErrorDetail('');
    try {
      // 末尾スラッシュを追加 (/applications/)
      const res = await fetch(`${API_URL}/applications/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          word: item.word,
          reading: item.reading,
          user_id: userId // ユーザーIDを付与
        })
      });
      if (res.ok) {
        setStatus('done');
      } else {
        const text = await res.text();
        setStatus('error');
        setErrorDetail(text); // 詳細を保存
        console.error("Application failed:", text);
      }
    } catch (e) {
      console.error(e);
      setStatus('error');
      setErrorDetail(e.message);
    }
  };

  return (
    <div className="flex items-center justify-between p-3 border-b border-gray-100 last:border-0">
      <div className="flex flex-col">
        <span className="font-bold text-gray-700">{item.word}</span>
        <span className="text-[10px] text-gray-400">{item.reason}</span>
      </div>
      {status === 'idle' && (
        <button
          onClick={handleApply}
          className="bg-blue-50 text-blue-600 text-xs font-bold px-3 py-1.5 rounded-lg hover:bg-blue-100 transition-colors flex items-center gap-1"
        >
          <Send className="w-3 h-3" />
          申請
        </button>
      )}
      {status === 'applying' && <span className="text-xs text-gray-400 animate-pulse">送信中...</span>}
      {status === 'done' && <span className="text-xs text-green-500 font-bold">申請済</span>}
      {status === 'error' && (
        <span className="text-xs text-red-500 font-bold" title={errorDetail}>
          エラー
        </span>
      )}
    </div>
  );
}

export default function ResultScreen({ score, history, rejectedWords = [], applicationCandidates = [], onRetry, highScores, onShowRanking, userId }) {
  const getFeedback = (s) => {
    if (s === 0) return "まずは1個目指そう！";
    if (s < 5) return "ウォーミングアップ完了！";
    if (s < 10) return "ナイス・ブレイン！";
    if (s < 15) return "素晴らしい瞬発力！";
    return "ことバトルマスター！！";
  };

  const pastBest = highScores.length > 0 ? highScores[0].score : 0;
  const isNewRecord = score > pastBest && score > 0;

  return (
    <div className="text-center space-y-6 animate-fade-in py-2">
      <div className="space-y-1">
        <Trophy className={`w-16 h-16 mx-auto drop-shadow-md ${isNewRecord ? 'text-red-500 animate-bounce' : 'text-yellow-400'}`} />
        <h2 className="text-3xl font-black text-gray-800 tracking-tight">TIME UP!</h2>
        <p className="text-orange-500 font-bold">{getFeedback(score)}</p>

        {isNewRecord && (
          <div className="text-red-500 font-black text-lg animate-pulse">
            NEW RECORD!
          </div>
        )}
      </div>

      <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-3xl border border-orange-200 shadow-sm relative overflow-hidden">
        <div className="absolute top-0 right-0 opacity-10 transform translate-x-4 -translate-y-4">
          <Brain className="w-32 h-32" />
        </div>
        <p className="text-sm text-gray-500 mb-1 font-bold uppercase tracking-widest">SCORE</p>
        <p className="text-6xl font-black text-gray-800 leading-none">
          {score}<span className="text-lg font-medium text-gray-500 ml-2">pt</span>
        </p>
        <div className="mt-3 flex items-center justify-center gap-2">
          <span className="text-gray-400 text-xs font-bold uppercase tracking-wider">Word Count</span>
          <span className="text-xl font-black text-gray-600 bg-white px-3 py-0.5 rounded-lg border border-gray-100 shadow-sm">
            {history.length - rejectedWords.length} <span className="text-xs font-normal text-gray-400">words</span>
          </span>
        </div>

        {rejectedWords.length > 0 && (
          <div className="mt-4 bg-red-50 text-red-500 text-xs py-2 px-3 rounded-lg border border-red-100 flex items-center justify-center gap-2">
            <AlertCircle className="w-3 h-3" />
            最終判定で {rejectedWords.length}語 が除外されました
          </div>
        )}

        {applicationCandidates.length > 0 && (
          <div className="mt-4 text-left">
            <p className="text-xs text-gray-400 mb-2 font-bold uppercase text-center">申請可能な単語</p>
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
              {Array.from(new Map(applicationCandidates.map(item => [item.word, item])).values()).map((item, idx) => (
                <ApplicationRow key={idx} item={item} userId={userId} />
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={onShowRanking}
          className="bg-yellow-100 hover:bg-yellow-200 text-yellow-800 font-bold py-3 px-4 rounded-xl shadow-sm transition-transform active:scale-95 flex items-center justify-center gap-2 text-sm"
        >
          <Crown className="w-4 h-4" />
          ランキング
        </button>
        <button
          onClick={onRetry}
          className="bg-gray-800 hover:bg-gray-900 text-white font-bold py-3 px-4 rounded-xl shadow-lg transition-transform active:scale-95 flex items-center justify-center gap-2 text-sm"
        >
          <RotateCcw className="w-4 h-4" />
          もう一度
        </button>
      </div>

      <div className="text-left bg-white p-4 rounded-xl border border-gray-100 shadow-sm max-h-[150px] overflow-y-auto">
        <p className="text-xs text-gray-400 mb-2 font-bold uppercase">あなたの回答</p>
        <div className="flex flex-wrap gap-2">
          {history.length > 0 ? history.map((word, idx) => {
            const isRejected = rejectedWords.includes(word);
            return (
              <span key={idx} className={`text-sm px-2 py-1 rounded border overflow-hidden relative ${isRejected ? 'bg-red-50 text-gray-400 border-red-100 line-through decoration-red-400 opacity-75' : 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                {word}
              </span>
            );
          }) : <span className="text-sm text-gray-400">なし</span>}
        </div>
      </div>
    </div>
  );
}
